/** @jsxImportSource @emotion/react */
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { HazardScenarioGridProps } from '../types/hazard.types'
import { formatParamLabel, formatRowLabel } from '../utils/hazardScenarioGrid.utils'
import {
  axisLabelCellStyles,
  colHeaderCellStyles,
  emptyCellLabelStyles,
  gridVars,
  rowHeaderCellStyles,
  scenarioCellBaseStyles,
  scenarioCellContentStyles,
  scenarioCellHighlightedStyles,
  scenarioCellSelectedStyles,
  tableContainerStyles,
  tableStyles,
  yearSpacerCellStyles,
} from './HazardScenarioGrid.styles'
import { HazardScenarioPill } from './HazardScenarioPill'

export const HazardScenarioGrid = ({
  grid,
  selectedAssetTag,
  selectedAssetTags,
  onSelect,
  emptyState,
}: HazardScenarioGridProps) => {
  const { colValues, rowValues, grid: cells, rowKey, colKey } = grid
  const isAssetSelected = (assetTag: string) =>
    selectedAssetTags ? selectedAssetTags.includes(assetTag) : assetTag === selectedAssetTag

  if (rowValues.length === 0) {
    return <>{emptyState}</>
  }

  return (
    <TableContainer css={[gridVars, tableContainerStyles]}>
      <Table size="small" css={tableStyles}>
        <TableHead>
          {/* Axis label row */}
          <TableRow>
            <TableCell css={[yearSpacerCellStyles, axisLabelCellStyles]} align="right">
              {formatParamLabel(rowKey)}
            </TableCell>
            <TableCell colSpan={colValues.length} css={axisLabelCellStyles} align="center">
              {formatParamLabel(colKey)}
            </TableCell>
          </TableRow>

          {/* Column values row */}
          <TableRow>
            <TableCell css={yearSpacerCellStyles} />
            {colValues.map((col) => (
              <TableCell
                key={String(col)}
                component="th"
                scope="col"
                align="center"
                css={colHeaderCellStyles}
              >
                {String(col)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rowValues.map((row, rowIndex) => (
            <TableRow key={String(row)}>
              <TableCell component="th" scope="row" css={rowHeaderCellStyles}>
                {formatRowLabel(row, rowKey)}
              </TableCell>

              {colValues.map((_, colIndex) => {
                const scenarioCells = cells[rowIndex]?.[colIndex] ?? []
                const isSelected = scenarioCells.some((c) => isAssetSelected(c.assetTag))
                const hasScenarios = scenarioCells.length > 0

                return (
                  <TableCell
                    key={`${String(row)}-${String(colValues[colIndex])}`}
                    align="center"
                    css={[
                      scenarioCellBaseStyles,
                      hasScenarios && scenarioCellHighlightedStyles,
                      isSelected && scenarioCellSelectedStyles,
                    ]}
                  >
                    {scenarioCells.length === 0 ? (
                      <Box css={scenarioCellContentStyles}>
                        <Typography
                          variant="caption"
                          component="span"
                          css={emptyCellLabelStyles}
                          aria-label="No scenarios"
                        >
                          –
                        </Typography>
                      </Box>
                    ) : (
                      <Box css={scenarioCellContentStyles}>
                        {scenarioCells.map((cell) => (
                          <HazardScenarioPill
                            key={cell.assetTag}
                            cell={cell}
                            isSelected={isAssetSelected(cell.assetTag)}
                            onSelect={onSelect}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
